import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Badge } from './badge';
import { Button } from './button';

export interface MultiSelectProps {
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder: string;
  options: { value: string; label: string }[];
  className?: string;
  /**
   * Lands on the trigger button, which is a labelable element -- so a sibling
   * `<Label htmlFor>` names the control the same way it does for a `Select`.
   * Without it the label has nothing to point at and a screen reader announces
   * only the placeholder, never the field it belongs to.
   */
  id?: string;
}

export const MultiSelect = ({
  value,
  onValueChange,
  placeholder,
  options,
  className,
  id,
}: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onValueChange(value.filter((v) => v !== optionValue));
    } else {
      onValueChange([...value, optionValue]);
    }
  };

  /**
   * Labels for the current selection, in the order the options are declared
   * rather than the order they were clicked — so the trigger text stays stable
   * as a user toggles items on and off.
   *
   * Any selected value with no matching option is appended as-is. That happens
   * when a selection is restored from a URL or storage before the option list
   * has loaded, and dropping it silently would under-report the count.
   */
  const unmatchedValues = value.filter((v) => !options.some((opt) => opt.value === v));
  const displayLabels = [
    ...options.filter((opt) => value.includes(opt.value)).map((opt) => opt.label),
    ...unmatchedValues,
  ];

  /**
   * Report unmatched selections. Reaching here means a value was selected that
   * the option list does not contain — in practice a stale or hand-edited URL
   * query param, or stale sessionStorage. The control degrades gracefully by
   * showing the raw value, which is deliberate, but silence would hide a real
   * data problem: the user sees a filter applied that the backend may reject.
   *
   * Keyed on the values themselves via a ref rather than logged during render,
   * so toggling other options does not re-log, and React's double-invoked
   * effects in development do not duplicate it.
   *
   * `console.warn` is the only reporting channel this app has — there is no
   * client-side logger or telemetry wired up. `VITE_OTEL_EXPORTER_OTLP_ENDPOINT`
   * exists in `.env` but is referenced by no code, so browser traces have no
   * destination. If that is ever connected, this is a signal worth forwarding.
   */
  const lastWarnedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = unmatchedValues.join('\u0000');
    if (key === lastWarnedRef.current) return;
    lastWarnedRef.current = key;
    if (unmatchedValues.length === 0) return;

    // biome-ignore lint/suspicious/noConsole: the only reporting channel this app has; no client logger or telemetry is wired up
    console.warn(
      `[MultiSelect] ${unmatchedValues.length} selected value(s) match no option and are shown as-is:`,
      unmatchedValues,
      `| control: "${placeholder}" | known options: ${options.map((o) => o.value).join(', ') || '(none yet)'}`
    );
  }, [unmatchedValues, options, placeholder]);

  return (
    <div className={`relative ${className || ''}`} ref={dropdownRef}>
      <Button
        variant="outline"
        {...(id && { id })}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full justify-between"
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate">
            {value.length === 0 ? placeholder : displayLabels.join(', ')}
          </span>
          {/* Count of selected options. Without it a single selection is
              indistinguishable from a single-select showing the same value, and
              the control gives no hint that more than one choice is possible. */}
          {value.length > 0 && (
            <Badge variant="secondary" aria-label={`${value.length} selected`}>
              {value.length}
            </Badge>
          )}
        </span>
        <ChevronDown className="h-4 w-4" />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-auto">
          <div className="p-1">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className="flex items-center justify-between w-full px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground rounded-sm cursor-pointer"
                onClick={() => handleSelect(option.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(option.value);
                  }
                }}
              >
                <span>{option.label}</span>
                {value.includes(option.value) && (
                  <div className="h-2 w-2 rounded border-2 border-primary bg-primary" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
